import { ref } from "vue";
import { useDebounceFn } from "@vueuse/core";

/**
 * Owns the chatterbox suggestion paired with the query it was fetched for. The
 * landing omnisearch reaches for the NLP fallback only when nothing local
 * matches — the suggestion is for the dead-end case, debounced so it doesn't
 * fire on every keystroke. The `{ query, node }` pairing lets the consumer
 * append the node only under its originating query, so a slower suggestion for
 * query A can never surface beneath query B.
 *
 * @param {Object} options
 * @param {Object} options.store - the omnisearch store (`hasLocalMatch`, `fetchSuggestedFilter`)
 * @param {import("vue").Ref<String>} options.searchQuery - the live search query ref
 * @param {Number} [options.delay] - debounce window in ms
 * @returns {{ suggestion: import("vue").Ref<{query: String, node: Object}|null>, request: (query: String) => void, clear: () => void }}
 */
export function useOmnisearchSuggestions({ store, searchQuery, delay = 500 }) {
  const suggestion = ref(null);
  let controller = null;

  const fetchSuggested = useDebounceFn(async (query) => {
    // Abort any prior in-flight suggestion so a slower earlier request can't land
    // after a newer query.
    controller?.abort();
    controller = null;
    if (!query || store.hasLocalMatch(query)) {
      return;
    }

    controller = new AbortController();
    const { signal } = controller;

    // Best-effort: a failing suggestion service just means no suggestion.
    const node = await store.fetchSuggestedFilter(query, { signal }).catch(() => null);
    // Bail if the input moved on while the request was in flight.
    if (node && query === searchQuery.value.trim()) {
      suggestion.value = { query, node };
    }
  }, delay);

  function request(query) {
    // Clear synchronously so a suggestion fetched for the previous query can't
    // linger under the new input before the debounced fetch resolves.
    suggestion.value = null;
    fetchSuggested(query);
  }

  function clear() {
    controller?.abort();
    controller = null;
    suggestion.value = null;
  }

  return { suggestion, request, clear };
}
